import React, { useEffect, useState, useCallback, useRef } from 'react'
import { FaDice } from 'react-icons/fa'
import { Box, Button, Heading, Icon, Text, VStack, useColorModeValue } from '@chakra-ui/react'

import ChosenUoaImage from '../Components/ChosenUoaImage'
import PickerDrawFlip, { POOL_CAP, STRIP_MAX_W } from '../Components/PickerDrawFlip'
import { UOA_GALLERY_ITEMS, uoaItemToRollShim } from '../uoaGallery'
import { pickRandomSubset, randomInt, getBggGameCoverUrl, bggTextField } from '../Utils'

export default function Uoa() {
	const [activeCollection] = useState(() => UOA_GALLERY_ITEMS.map(uoaItemToRollShim))
	const [chosenGame, setChosenGame] = useState()
	const [rollVisual, setRollVisual] = useState(null)
	const rollSessionIdRef = useRef(0)
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

	const bodyMuted = useColorModeValue('gray.600', 'bgg.muted')
	const stripMuted = useColorModeValue('gray.600', 'gray.400')

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
		const apply = () => setPrefersReducedMotion(!!mq.matches)
		apply()
		if (mq.addEventListener) {
			mq.addEventListener('change', apply)
			return () => mq.removeEventListener('change', apply)
		}
		mq.addListener(apply)
		return () => mq.removeListener(apply)
	}, [])

	const buildRollPool = useCallback((collection) => {
		if (!collection.length) return []
		if (collection.length > POOL_CAP) return pickRandomSubset(collection, POOL_CAP)
		return collection
	}, [])

	const startRoll = useCallback(() => {
		if (!activeCollection.length) {
			setChosenGame(undefined)
			setRollVisual(null)
			return
		}
		const rollPool = buildRollPool(activeCollection)
		if (!rollPool.length) {
			setChosenGame(undefined)
			setRollVisual(null)
			return
		}
		const winner = rollPool[randomInt(rollPool.length)]
		setChosenGame(undefined)
		setRollVisual(null)
		rollSessionIdRef.current += 1
		const sessionId = rollSessionIdRef.current

		if (prefersReducedMotion) {
			setChosenGame(winner)
			return
		}
		setRollVisual({ rollPool, winner, sessionId })
	}, [activeCollection, buildRollPool, prefersReducedMotion])

	const handleRollComplete = useCallback((winner, sessionId) => {
		if (sessionId !== rollSessionIdRef.current) return
		setChosenGame(winner)
		setRollVisual(null)
	}, [])

	useEffect(() => {
		startRoll()
	}, [activeCollection, startRoll])

	const isRolling = rollVisual != null
	const hasGallery = activeCollection.length > 0

	return (
		<VStack spacing={8} align="stretch">
			<VStack spacing={3} align="flex-start">
				<Heading size="lg">UoA</Heading>
				<Text color={bodyMuted} fontSize="md" lineHeight="tall">
					Náhodný výběr z galerie obrázků — stejný losovací pás jako v Pickeru, bez napojení na BGG.
				</Text>
			</VStack>

			{!hasGallery ? (
				<Text color={bodyMuted} fontSize="sm">
					Ve složce galerie nejsou žádné obrázky.
				</Text>
			) : (
				<VStack spacing={4} align="stretch">
					<Button
						colorScheme="brand"
						variant="ghost"
						onClick={startRoll}
						isDisabled={isRolling}
						size="lg"
						rounded="lg"
						rightIcon={<Icon as={FaDice} />}
						aria-busy={isRolling}
					>
						Roll again
					</Button>
					<Text fontSize="xs" color={stripMuted} lineHeight="short" maxW="lg">
						Každé losování náhodně vybere jeden obrázek z galerie (pool je omezený jako v Pickeru, max. {POOL_CAP}{' '}
						náhledů ve stripu).
					</Text>
				</VStack>
			)}

			<Box
				id="picker-roll-again"
				aria-hidden="true"
				h="1px"
				w="full"
				maxW="100%"
				flexShrink={0}
				sx={{ scrollMarginTop: { base: '5.5rem', md: '6rem' } }}
			/>

			{rollVisual ? (
				<PickerDrawFlip
					rollPool={rollVisual.rollPool}
					winner={rollVisual.winner}
					sessionId={rollVisual.sessionId}
					onComplete={handleRollComplete}
				/>
			) : null}

			{chosenGame && hasGallery ? (
				<Box
					display="flex"
					justifyContent="center"
					w="full"
					maxW={`${STRIP_MAX_W}px`}
					mx="auto"
					alignSelf="center"
					aria-live="polite"
					aria-atomic="true"
				>
					<ChosenUoaImage
						src={getBggGameCoverUrl(chosenGame)}
						title={bggTextField(chosenGame.name) || chosenGame['@_objectid']}
						maxW="100%"
					/>
				</Box>
			) : null}
		</VStack>
	)
}
